/**
 * Every wait, drawn as the thing that is coming.
 *
 * The fleet ranks loading states once (STYLE.md): a content-shaped skeleton first, a spinner second,
 * the word "Loading…" never — and on a public page it matters more than in any console. Somebody who
 * followed a link out of their inbox decides in the first second whether the page is the brand's, and
 * a spinner on a blank ground is what a phishing page looks like.
 *
 * So a `Skeleton` is a SHAPE of a real component at that component's real size — a `field` is a
 * field's height, a `cover` is a 3:4 frame, a `title` is a heading's line — and a placeholder is built
 * from them inside a `Stack busy` / `Row busy`, which announces it and makes every block breathe
 * together. Nothing moves when the answer lands. The colour and the pulse live here and nowhere else.
 */
import { cx, mod } from './cx';

export type SkeletonShape =
  | 'micro'
  | 'line'
  | 'title'
  | 'display'
  | 'price'
  | 'field'
  | 'button'
  | 'pill'
  | 'disc'
  | 'cover'
  | 'thumb';

/** How wide a line is, as a fraction of its container or a fixed step. */
export type SkeletonWidth =
  'full' | '4/5' | '3/4' | '2/3' | '3/5' | '1/2' | '1/3' | '1/4' | 'xs' | 'sm' | 'md' | 'lg';

/** A cover's proportions, as `Photo`'s: a wall of varied frames is drawn as one (3:4 by default). */
export type SkeletonRatio = 'square' | 'portrait' | 'landscape' | 'wide';

/** One block of a placeholder. */
export function Skeleton({
  shape = 'line',
  width = 'full',
  size,
  ratio,
}: {
  shape?: SkeletonShape | undefined;
  width?: SkeletonWidth | undefined;
  /** A disc's diameter: `md` a bar's mark, `lg` a large `Avatar`, `xl` a link page's. */
  size?: 'md' | 'lg' | 'xl' | undefined;
  /** A `cover`'s proportions, so a masonry placeholder is as uneven as the wall it stands for. */
  ratio?: SkeletonRatio | undefined;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'a-skeleton',
        mod('a-skeleton', 'shape', shape),
        mod('a-skeleton', 'width', width),
        size && mod('a-skeleton', 'size', size),
        ratio && mod('a-skeleton', 'ratio', ratio),
      )}
    />
  );
}

/** A product card's placeholder: the 3:4 frame, the title, the subtitle, the price. */
export function CardSkeleton() {
  return (
    <span className="a-card-skeleton" aria-hidden="true">
      <Skeleton shape="cover" />
      <Skeleton shape="line" width="4/5" />
      <Skeleton shape="micro" width="3/5" />
      <Skeleton shape="line" width="1/3" />
    </span>
  );
}
